/**
 * Fountain plain-text export — an escape hatch, not a lossless format. Elements
 * are force-marked (`.scene`, `@CHARACTER`, `>TRANSITION`) so mixed-case and
 * Serbian sluglines (ENT./EKST.) export unambiguously. Dialogue blocks stay
 * contiguous; empty spacing paragraphs become Fountain's blank-line separators.
 */
import type { Para } from "../format/types";
import type { TitleValues } from "./titlepage";

function paraText(p: Para): string {
  return p.runs.map((r) => r.text).join("");
}

/** Fountain title-page keys, in output order. */
const TITLE_MAP: Array<[keyof TitleValues, string]> = [
  ["Title", "Title"],
  ["Author", "Author"],
  ["Draft", "Draft date"],
  ["Contact", "Contact"],
  ["Copyright", "Copyright"],
];

function titlePageBlock(values: TitleValues | undefined): string {
  if (!values) return "";
  const lines: string[] = [];
  for (const [field, key] of TITLE_MAP) {
    const v = values[field];
    if (v && v.trim()) lines.push(`${key}: ${v.trim()}`);
  }
  return lines.length > 0 ? lines.join("\n") + "\n\n" : "";
}

export function toFountain(paras: Para[], titleValues?: TitleValues): string {
  const blocks: string[] = [];
  let dialogue: string[] | null = null;

  const flush = () => {
    if (dialogue) {
      blocks.push(dialogue.join("\n"));
      dialogue = null;
    }
  };

  for (const para of paras) {
    const text = paraText(para);
    switch (para.style) {
      case "Character":
        flush();
        dialogue = [`@${text}`];
        break;
      case "Parenthetical":
      case "Dialogue":
        if (dialogue) dialogue.push(text);
        else blocks.push(text);
        break;
      case "Scene Heading":
      case "Shot":
        flush();
        blocks.push(`.${text}`);
        break;
      case "Transition":
        flush();
        blocks.push(`>${text}`);
        break;
      case "Action":
        flush();
        blocks.push(text);
        break;
      default:
        // Normal Text / unknown: only emit if it carries real text.
        flush();
        if (text.trim()) blocks.push(text);
    }
  }
  flush();

  return titlePageBlock(titleValues) + blocks.join("\n\n") + "\n";
}
